/**
 * Capturing waiver consent before a member has an account, for the request that creates it.
 *
 * Account setup is the one flow where consent must be collected *before* the signer can
 * authenticate at all. A member in a pre-enrollment status (`pending` from an invite or
 * import, `incomplete` from the guest funnel) cannot hold a session — `validateAccessToken`
 * and `refresh` both refuse with `ACCOUNT_SETUP_REQUIRED` — so the waiver is read
 * anonymously, held in component state, and handed to the activation request itself.
 * See /docs/domains/authentication/identities.md.
 *
 * This exists rather than the checkout's `useWaiverSubmission` because that hook reads
 * consent out of `useBooking()`, and there is no BookingProvider on an auth page. The rule
 * both share is `isWaiverConsentComplete`, imported from the same place, so the client can
 * never disagree with itself about what "signed" means.
 *
 * **Reads the anonymous active-waiver list, never `useWaiverRequirements`.** That hook
 * swaps to the authenticated standing query the instant `isAuthenticated` flips, which on
 * this page happens mid-submit. Pinning to the anonymous list removes the swap entirely.
 *
 * Nothing is posted from here. The consent rides along with the activation request and is
 * written in the same transaction, because activation cannot be retried once it commits —
 * see `services/auth/account/orchestrators/completeAccountSetup`.
 *
 * Keep in step with `apps/mobile-app/src/features/waivers/useWaiverCapture.ts`.
 *
 * @module features/waivers/useWaiverCapture
 */

import { useCallback, useState } from 'react';
import { isWaiverConsentComplete } from '@butter/ui/domains/legal';
import { useActiveWaiversQuery } from '~/services/internalApi/waiver';
import type { ActiveWaiver, WaiverConsent, WaiverSignaturePaths } from '~/services/internalApi/waiver';

/** Consent keyed by `waiverVersionId`. Empty objects are module-scoped for stability. */
const NO_SIGNATURES: Record<number, WaiverSignaturePaths | null> = {};
const NO_AGREEMENTS: Record<number, boolean> = {};
const NO_WAIVERS: ActiveWaiver[] = [];
const NO_CONSENTS: WaiverConsent[] = [];

export interface WaiverCapture {
  waivers: ActiveWaiver[];
  /** A requirements list actually arrived. The safety question — see `satisfied`. */
  hasAnswer: boolean;
  isLoading: boolean;
  /** No list arrived and none is coming: failed, or paused offline. */
  answerMissing: boolean;
  retry: () => void;
  signatures: Record<number, WaiverSignaturePaths | null>;
  agreements: Record<number, boolean>;
  setSignature: (waiverVersionId: number, value: WaiverSignaturePaths | null) => void;
  setAgreement: (waiverVersionId: number, value: boolean) => void;
  /** Every required signature and checkbox is present, against an answer we have. */
  satisfied: boolean;
  /**
   * The consent to send with the activation request.
   *
   * Built from the waivers on screen, which is safe because the submit that carries it
   * is gated on `satisfied` — every listed waiver is complete by the time this is read.
   */
  capturedPayload: () => WaiverConsent[];
}

export function useWaiverCapture(clubId: number | null | undefined): WaiverCapture {
  const [signatures, setSignatures] = useState<Record<number, WaiverSignaturePaths | null>>(NO_SIGNATURES);
  const [agreements, setAgreements] = useState<Record<number, boolean>>(NO_AGREEMENTS);

  const query = useActiveWaiversQuery({ clubId, enabled: clubId != null });
  const waivers = query.waivers;
  const hasAnswer = query.data !== undefined;

  const setSignature = useCallback((waiverVersionId: number, value: WaiverSignaturePaths | null) => {
    setSignatures((previous) => ({ ...previous, [waiverVersionId]: value }));
  }, []);

  const setAgreement = useCallback((waiverVersionId: number, value: boolean) => {
    setAgreements((previous) => ({ ...previous, [waiverVersionId]: value }));
  }, []);

  /**
   * The consent to post, one entry per waiver on screen.
   *
   * Read at submit time only, and only behind `satisfied`, so an untouched waiver can
   * never reach the server — the checks there would refuse it. A version requiring
   * neither a signature nor an agreement is still sent, so the club gets its
   * acknowledgement row.
   */
  const capturedPayload = useCallback((): WaiverConsent[] => {
    if (clubId == null) return NO_CONSENTS;
    return waivers.map((waiver) => ({
      waiverVersionId: waiver.waiverVersionId,
      agreementAccepted: agreements[waiver.waiverVersionId] ?? false,
      signaturePaths: signatures[waiver.waiverVersionId] ?? null,
    }));
  }, [clubId, waivers, agreements, signatures]);

  /**
   * `hasAnswer &&`, never `!isLoading` and never `isSuccess` — a failed query is also
   * "not loading" and its list is `[]`, which reads as vacuously satisfied.
   * See /docs/conventions/react-state.md.
   *
   * The `clubId == null` branch is the exception, and it is deliberate: with no club
   * there is no waiver to fetch and the query never runs, so `hasAnswer` would be false
   * forever and a fail-closed caller could never finish setting up an account. Missing
   * club is already a hard error on these pages, surfaced by the flow itself.
   */
  const satisfied = clubId == null
    || (hasAnswer && waivers.every((waiver) => isWaiverConsentComplete({
      requireSignature: waiver.requireSignature,
      requireAgreement: waiver.requireAgreement,
      signature: signatures[waiver.waiverVersionId] ?? null,
      agreed: agreements[waiver.waiverVersionId] ?? false,
    })));

  return {
    waivers: clubId == null ? NO_WAIVERS : waivers,
    hasAnswer: clubId == null ? true : hasAnswer,
    isLoading: query.isLoading,
    // `!isLoading` rather than `isError`: an offline query is PAUSED, which react-query
    // reports as neither loading nor errored.
    answerMissing: clubId == null ? false : !hasAnswer && !query.isLoading,
    retry: query.refetch,
    signatures,
    agreements,
    setSignature,
    setAgreement,
    satisfied,
    capturedPayload,
  };
}
